import { useEffect, useMemo, useState } from 'react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  EmptyState,
  InfoCard,
  Progress,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { MetricChart } from './MetricChart';
import {
  ChartPayload,
  ChartRow,
  DEFAULT_RANGE,
  RESOURCE_ANNOTATION,
  SeriesResponse,
  TIME_RANGES,
  TimeRange,
  formatValue,
  hasNothing,
  isAzureMetricsAvailable,
  seriesUrl,
  toChartRows,
} from './lib';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: SeriesResponse };

/** Keeps a chart's content reachable without colour, hover or a pointer. */
const ChartTable = ({ chart }: { chart: ChartPayload }) => {
  const rows = toChartRows(chart.series);
  const columns: TableColumn<ChartRow>[] = [
    {
      title: 'Time',
      field: 't',
      render: row => new Date(row.t).toLocaleString(),
    },
    ...chart.series.map(s => ({
      title: s.name,
      field: s.name,
      render: (row: ChartRow) => formatValue(row[s.name] ?? null, chart.unit),
    })),
  ];
  return (
    <Table
      options={{ search: false, paging: true, pageSize: 5, padding: 'dense' }}
      columns={columns}
      data={rows}
    />
  );
};

export const EntityAzureMetrics = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [range, setRange] = useState<TimeRange>(DEFAULT_RANGE);
  const [environment, setEnvironment] = useState<string>();
  const [showTables, setShowTables] = useState(false);
  const [state, setState] = useState<State>({ kind: 'loading' });

  const entityRef = useMemo(() => stringifyEntityRef(entity), [entity]);
  const annotated = isAzureMetricsAvailable(entity);

  useEffect(() => {
    if (!annotated) {
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'loading' });

    const load = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('azure-metrics');
        const response = await fetchApi.fetch(
          seriesUrl(baseUrl, entityRef, range, environment),
        );
        if (!response.ok) {
          let detail = '';
          try {
            const body = await response.json();
            detail = body?.error?.message ?? body?.error ?? '';
          } catch {
            detail = '';
          }
          if (!cancelled) {
            setState({
              kind: 'error',
              message: detail || `Metric query failed (${response.status}).`,
            });
          }
          return;
        }
        const data = (await response.json()) as SeriesResponse;
        if (!cancelled) {
          setState({ kind: 'ready', data });
        }
      } catch {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: 'Could not reach the metrics service.',
          });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [annotated, discoveryApi, entityRef, environment, fetchApi, range]);

  if (!annotated) {
    return (
      <EmptyState
        title="No Azure resource for this entity"
        missing="info"
        description={`Add an ${RESOURCE_ANNOTATION} annotation with the resource id to chart its metrics here.`}
      />
    );
  }

  const dashboardHref =
    state.kind === 'ready' && state.data.dashboardUrl
      ? `${state.data.dashboardUrl}${
          state.data.dashboardUrl.includes('?') ? '&' : '?'
        }from=${range.from}&to=${range.to}`
      : undefined;

  return (
    <Grid container spacing={3}>
      {/* Controls in one row above the charts, never per chart. */}
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              select
              size="small"
              variant="outlined"
              label="Time range"
              value={range.label}
              onChange={event =>
                setRange(
                  TIME_RANGES.find(r => r.label === event.target.value) ??
                    DEFAULT_RANGE,
                )
              }
              style={{ minWidth: 180 }}
            >
              {TIME_RANGES.map(option => (
                <MenuItem key={option.label} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {state.kind === 'ready' && state.data.environments.length > 1 && (
            <Grid item>
              <TextField
                select
                size="small"
                variant="outlined"
                label="Environment"
                value={state.data.environment}
                onChange={event => setEnvironment(event.target.value)}
                style={{ minWidth: 150 }}
              >
                {state.data.environments.map(env => (
                  <MenuItem key={env} value={env}>
                    {env}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item>
            <Button size="small" onClick={() => setShowTables(v => !v)}>
              {showTables ? 'Show charts' : 'Show tables'}
            </Button>
          </Grid>
          <Grid item style={{ flexGrow: 1 }} />
          {state.kind === 'ready' && (
            <Grid item>
              <Typography variant="caption" color="textSecondary">
                {state.data.resource.name} · {state.data.resource.type}
              </Typography>
            </Grid>
          )}
          {dashboardHref && (
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                href={dashboardHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Grafana
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>

      {state.kind === 'loading' && (
        <Grid item xs={12}>
          <Progress />
        </Grid>
      )}

      {state.kind === 'error' && (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            {state.message}
          </Typography>
        </Grid>
      )}

      {state.kind === 'ready' && hasNothing(state.data) && (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Azure Monitor returned no samples for this resource in this range.
            Try a longer range — platform metrics are kept for a limited window.
          </Typography>
        </Grid>
      )}

      {state.kind === 'ready' &&
        state.data.charts.map(chart => (
          <Grid item key={chart.key} xs={12} md={6}>
            <InfoCard title={chart.title}>
              {showTables ? (
                <ChartTable chart={chart} />
              ) : (
                <MetricChart
                  series={chart.series}
                  unit={chart.unit}
                  range={range}
                />
              )}
            </InfoCard>
          </Grid>
        ))}

      {state.kind === 'ready' && state.data.unavailable.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            No samples from Azure Monitor for:{' '}
            {state.data.unavailable.join(', ')}. These metrics may need enhanced
            monitoring enabled on the resource.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};
